import { c as _c } from "react/compiler-runtime";
import { logEvent } from '@/analytics/events';
import { GetFaqCategoryQuery, GetFaqItemQuery } from '@/api/sanity/generated';
import ChevronUpIcon from '@/assets/icons/heroIcons/Chevron-up.svg';
import { Link } from '@/components/links';
import { MarkdownContent } from '@/components/Markdown/Markdown';
import { Text } from '@/components/typography';
import { LINKS } from '@/constants/links';
import { useInViewport } from '@/hooks/useInViewport';
import { mergeClasses } from '@/utils/style';
import { motion, AnimatePresence } from 'framer-motion';
import type { MDXRemoteSerializeResult } from 'next-mdx-remote';
import { useRouter } from 'next/router';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Category } from './NextCategory';
type Props = NonNullable<NonNullable<NonNullable<NonNullable<NonNullable<GetFaqCategoryQuery>['allFaqCategory']>[number]>['questions']>[number]> & {
  variant: 'closed' | 'open';
  questionDetail?: NonNullable<NonNullable<GetFaqItemQuery['allFaqQuestion']>>[number] & {
    descriptionMarkdown?: MDXRemoteSerializeResult;
    linkedFrom: Category;
  };
};
const scrollIntoViewWithOffset = (element: HTMLElement, offset = 0) => {
  setTimeout(() => {
    const elementPosition = element.getBoundingClientRect().top;
    const offsetPosition = elementPosition - offset;
    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth'
    });
  }, 100);
};

// Malwarelytics features from app close reason. Always sync with current one in client
const enabledFeatures = ['root', 'adb', 'emulator', 'debugger', 'jailbreak', 'reverseEngineeringTools', 'repackage'];
function isFeatureEnabled(feature: string | string[]) {
  if (Array.isArray(feature)) {
    return feature.every(f => enabledFeatures.includes(f));
  }
  return enabledFeatures.includes(feature);
}
export function DetailQuestion(props) {
  const $ = _c(43);
  const {
    variant,
    name,
    questionDetail,
    slug
  } = props;
  const router = useRouter();
  const {
    t
  } = useTranslation("faq");
  const isActive = variant === "open";
  const ref = useRef(null);
  const {
    isInViewport
  } = useInViewport(ref);
  const categoryLink = questionDetail?.linkedFrom?.slug.current;
  let t0;
  let t1;
  if ($[0] !== isActive || $[1] !== isInViewport) {
    t0 = () => {
      if (!isInViewport && isActive && ref.current) {
        scrollIntoViewWithOffset(ref.current, 150);
      }
    };
    t1 = [isInViewport, isActive];
    $[0] = isActive;
    $[1] = isInViewport;
    $[2] = t0;
    $[3] = t1;
  } else {
    t0 = $[2];
    t1 = $[3];
  }
  useEffect(t0, t1);
  const faqAdditionalInfo = router.query?.reason;
  const t2 = `questionDetail${slug || ""}`;
  const t3 = `questionDetail${slug || ""}`;
  let t4;
  if ($[4] !== categoryLink || $[5] !== isActive || $[6] !== slug?.current) {
    t4 = isActive ? LINKS().faqCategory(categoryLink || "") : slug?.current || "";
    $[4] = categoryLink;
    $[5] = isActive;
    $[6] = slug?.current;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== isActive || $[9] !== name) {
    t5 = () => {
      if (!isActive && name) {
        logEvent("select_faq", {
          action: name
        });
      }
    };
    $[8] = isActive;
    $[9] = name;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const t6 = !isActive && "hover:bg-blue-100";
  let t7;
  if ($[11] !== t6) {
    t7 = mergeClasses("flex w-full items-center justify-between bg-white p-4 no-underline transition md:p-6", t6);
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== name) {
    t8 = <h2 className="text-sm font-semibold whitespace-normal text-gray-900 md:text-xl">{name}</h2>;
    $[13] = name;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  const t9 = isActive ? 0 : 180;
  let t10;
  if ($[15] !== t9) {
    t10 = {
      rotate: t9
    };
    $[15] = t9;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  const t11 = isActive ? 0 : 180;
  let t12;
  if ($[17] !== t11) {
    t12 = {
      rotate: t11
    };
    $[17] = t11;
    $[18] = t12;
  } else {
    t12 = $[18];
  }
  const t13 = isActive ? "pr-2" : "pl-2";
  let t14;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <ChevronUpIcon className={mergeClasses("min-h-[24px] min-w-[24px] md:min-h-[40px] md:min-w-[40px]")} />;
    $[19] = t14;
  } else {
    t14 = $[19];
  }
  let t15;
  if ($[20] !== t10 || $[21] !== t12 || $[22] !== t13) {
    t15 = <motion.div initial={t10} animate={t12} className={t13}>{t14}</motion.div>;
    $[20] = t10;
    $[21] = t12;
    $[22] = t13;
    $[23] = t15;
  } else {
    t15 = $[23];
  }
  let t16;
  if ($[24] !== t15 || $[25] !== t2 || $[26] !== t3 || $[27] !== t4 || $[28] !== t5 || $[29] !== t7 || $[30] !== t8 || $[31] !== variant) {
    t16 = <Link analyticsParam={t2} testId={t3} to={t4} onClick={t5} data-variant={variant} scroll={false} className={t7}>{t8}{t15}</Link>;
    $[24] = t15;
    $[25] = t2;
    $[26] = t3;
    $[27] = t4;
    $[28] = t5;
    $[29] = t7;
    $[30] = t8;
    $[31] = variant;
    $[32] = t16;
  } else {
    t16 = $[32];
  }
  let t17;
  if ($[33] !== faqAdditionalInfo || $[34] !== isActive || $[35] !== questionDetail || $[36] !== t) {
    t17 = isActive && <motion.section key="content" initial="collapsed" animate="open" exit="collapsed" variants={{
      open: {
        opacity: 1,
        height: "auto"
      },
      collapsed: {
        opacity: 0,
        height: 0
      }
    }} className="w-full"><div className="px-4 pb-4 whitespace-normal md:px-6 md:pb-6">{faqAdditionalInfo && isFeatureEnabled(faqAdditionalInfo) && <Text variant="p4" className="mb-2">{t("additionalFeatureEnabled", {
            feature: faqAdditionalInfo
          })}</Text>}{questionDetail?.descriptionMarkdown && <MarkdownContent content={questionDetail.descriptionMarkdown} />}</div></motion.section>;
    $[33] = faqAdditionalInfo;
    $[34] = isActive;
    $[35] = questionDetail;
    $[36] = t;
    $[37] = t17;
  } else {
    t17 = $[37];
  }
  let t18;
  if ($[38] !== t17) {
    t18 = <AnimatePresence initial={false}>{t17}</AnimatePresence>;
    $[38] = t17;
    $[39] = t18;
  } else {
    t18 = $[39];
  }
  let t19;
  if ($[40] !== t16 || $[41] !== t18) {
    t19 = <div className="text shadow-card flex scroll-mt-16 flex-col items-center justify-between overflow-hidden rounded-2xl bg-white" ref={ref}>{t16}{t18}</div>;
    $[40] = t16;
    $[41] = t18;
    $[42] = t19;
  } else {
    t19 = $[42];
  }
  return t19;
}